/**
 * Bundled sample DEVICES (implants + external clinical devices) — demo fallback.
 *
 * Used by DevicesPanel when the deployed site cannot reach the clinical API:
 * the section renders these contract-exact samples with a "Sample data" badge
 * (prov.sampleData).
 *
 * Device master list reference: prototype DEVICES_SY / DEVICE_SPECIALTIES
 * (75 device categories across 13 clinical specialties, from
 * SY_PHR_Clinical_Device_Master_v0_1.xlsx). The implantable / highVis flags
 * below come from that workbook: high-visibility matters because an implanted
 * device may affect MRI, surgery, electrocautery, emergency care, or
 * medication/procedure decisions.
 *
 * No device router exists yet, so the panel attempts the future
 * `GET /api/v1/patients/{id}/devices → { items, total }` contract first and
 * falls back here on failure.
 */

export type DeviceMriStatus = 'safe' | 'conditional' | 'unsafe' | 'unknown'

export type DeviceStatus = 'active' | 'removed' | 'replaced' | 'inactive'

export type DeviceView = {
  id: string
  patient_id: string
  /** Device type code from the SY clinical device master (e.g. SY-DEV-0001). */
  code: string
  device_type_en: string
  device_type_ar: string
  specialty?: string | null
  /** True when the device is (or was) inside the body — drives the alert dot. */
  implanted: boolean
  /** High-visibility per the workbook: affects MRI / surgery / emergency care. */
  high_visibility?: boolean
  implant_date?: string | null
  removal_replacement_date?: string | null
  body_site?: string | null
  device_name_model?: string | null
  manufacturer?: string | null
  udi_di?: string | null
  serial_lot?: string | null
  status: DeviceStatus
  mri_status: DeviceMriStatus
  indication?: string | null
  notes?: string | null
  created_at?: string | null
  updated_at?: string | null
}

const PATIENT_ID = '7f381bff-5fa9-4fee-92a0-f8e21a163e19'

/**
 * Six realistic rows covering all four MRI states and every device status.
 * Codes and EN/AR names are pulled from the prototype's DEVICES_SY master
 * (SY-DEV-0001 Pacemaker, 0013 Total hip prosthesis, 0048 IUD, 0034 Cochlear
 * implant, 0006 Coronary stent, 0049 Subdermal contraceptive implant).
 */
export const SAMPLE_DEVICES: DeviceView[] = [
  {
    id: '9d000004-0000-4000-8000-000000000001',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0001',
    device_type_en: 'Pacemaker',
    device_type_ar: 'ناظمة قلبية',
    specialty: 'Cardiology',
    implanted: true,
    high_visibility: true,
    implant_date: '2023-11-14',
    removal_replacement_date: null,
    body_site: 'left prepectoral pocket',
    device_name_model: 'Medtronic Azure W2DR01',
    manufacturer: 'Medtronic',
    udi_di: '00643169001234',
    serial_lot: 'PMC-847201',
    status: 'active',
    mri_status: 'unsafe',
    indication: 'Symptomatic sick sinus syndrome',
    notes: null,
  },
  {
    id: '9d000004-0000-4000-8000-000000000002',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0034',
    device_type_en: 'Cochlear implant',
    device_type_ar: 'غرسة قوقعة',
    specialty: 'ENT/Audiology',
    implanted: true,
    high_visibility: true,
    implant_date: '2019-06-03',
    removal_replacement_date: null,
    body_site: 'right temporal bone (cochlea)',
    device_name_model: 'Cochlear Nucleus CI532',
    manufacturer: 'Cochlear Ltd',
    udi_di: '00761823098765',
    serial_lot: 'CI-2019-5523',
    status: 'active',
    mri_status: 'unsafe',
    indication: 'Bilateral profound sensorineural hearing loss',
    notes: 'Magnet removable for MRI only per manufacturer protocol.',
  },
  {
    id: '9d000004-0000-4000-8000-000000000003',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0013',
    device_type_en: 'Total hip prosthesis',
    device_type_ar: 'مفصل ورك اصطناعي كامل',
    specialty: 'Orthopedics',
    implanted: true,
    high_visibility: true,
    implant_date: '2016-09-21',
    removal_replacement_date: null,
    body_site: 'left hip',
    device_name_model: 'Zimmer Biomet Taperloc Complete',
    manufacturer: 'Zimmer Biomet',
    udi_di: '00688999045678',
    serial_lot: 'THP-16-9930',
    status: 'active',
    mri_status: 'conditional',
    indication: 'End-stage osteoarthritis of the left hip',
    notes: 'MR Conditional — scanning permitted under stated conditions only.',
  },
  {
    id: '9d000004-0000-4000-8000-000000000004',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0006',
    device_type_en: 'Coronary stent',
    device_type_ar: 'دعامة تاجية',
    specialty: 'Cardiology',
    implanted: true,
    high_visibility: true,
    implant_date: '2024-02-10',
    removal_replacement_date: null,
    body_site: 'left anterior descending artery',
    device_name_model: 'Xience Alpine 3.0 × 18 mm',
    manufacturer: 'Abbott',
    udi_di: '00884873022334',
    serial_lot: 'STENT-24-1187',
    status: 'active',
    mri_status: 'conditional',
    indication: 'Single-vessel coronary artery disease',
    notes: 'Safe for MRI at 1.5 T / 3 T ≥ 1 month post-implant.',
  },
  {
    id: '9d000004-0000-4000-8000-000000000005',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0048',
    device_type_en: 'Intrauterine device (IUD)',
    device_type_ar: 'لولب رحمي',
    specialty: "Women's Health",
    implanted: true,
    high_visibility: true,
    implant_date: '2025-04-02',
    removal_replacement_date: null,
    body_site: 'uterine cavity',
    device_name_model: 'Mirena 52 mg LNG-IUS',
    manufacturer: 'Bayer',
    udi_di: '04019397011223',
    serial_lot: 'IUD-25-44210',
    status: 'active',
    mri_status: 'safe',
    indication: 'Contraception',
    notes: null,
  },
  {
    id: '9d000004-0000-4000-8000-000000000006',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0049',
    device_type_en: 'Subdermal contraceptive implant',
    device_type_ar: 'غرسة مانعة للحمل تحت الجلد',
    specialty: "Women's Health",
    implanted: true,
    high_visibility: true,
    implant_date: '2025-04-02',
    removal_replacement_date: '2027-04-02',
    body_site: 'left upper arm (subdermal)',
    device_name_model: 'Nexplanon 68 mg',
    manufacturer: 'Organon',
    udi_di: '00738987033445',
    serial_lot: 'IMP-25-7734',
    status: 'active',
    mri_status: 'safe',
    indication: 'Contraception',
    notes: null,
  },
  {
    id: '9d000004-0000-4000-8000-000000000007',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0048',
    device_type_en: 'Intrauterine device (IUD)',
    device_type_ar: 'لولب رحمي',
    specialty: "Women's Health",
    implanted: true,
    high_visibility: true,
    implant_date: '2018-02-05',
    removal_replacement_date: '2020-11-17',
    body_site: 'uterine cavity',
    device_name_model: 'Copper T 380A',
    manufacturer: 'Pregna',
    udi_di: '08908990055667',
    serial_lot: 'IUD-18-1012',
    status: 'removed',
    mri_status: 'safe',
    indication: 'Contraception',
    notes: 'Removed electively at patient request.',
  },
  {
    id: '9d000004-0000-4000-8000-000000000008',
    patient_id: PATIENT_ID,
    code: 'SY-DEV-0049',
    device_type_en: 'Subdermal contraceptive implant',
    device_type_ar: 'غرسة مانعة للحمل تحت الجلد',
    specialty: "Women's Health",
    implanted: true,
    high_visibility: true,
    implant_date: '2020-11-17',
    removal_replacement_date: null,
    body_site: 'right upper arm (subdermal)',
    device_name_model: 'Implanon NXT 68 mg',
    manufacturer: 'Organon',
    udi_di: '00733867099887',
    serial_lot: 'IMP-20-3411',
    status: 'replaced',
    mri_status: 'safe',
    indication: 'Contraception',
    notes: 'Replaced with current implant on 2025-04-02.',
  },
]